
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageCircle, Send, Image, Paperclip, Phone, Video, MoreVertical } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { protectMessageContent, validateMessageContent, safeDisplayContent } from '@/utils/messageProtection';

interface Message {
  id: string;
  body_raw: string;
  body_masked: string;
  sender_id: string;
  chat_id: string;
  created_at: string;
  flagged: boolean;
}

interface Chat {
  id: string;
  listing_id: string;
  student_id: string;
  owner_id: string;
  blocked: boolean;
  created_at: string;
  last_message?: string;
  unread_count?: number;
  other_user_name?: string;
  other_user_avatar?: string;
  listing_title?: string;
}

const EnhancedMessaging = () => {
  const { user, profile } = useAuth();
  const { t } = useLanguage();
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      fetchChats();
    }
  }, [user]);

  useEffect(() => {
    if (selectedChat) {
      fetchMessages(selectedChat.id);
      
      // Set up polling for new messages instead of real-time subscription
      const messagePolling = setInterval(() => {
        fetchMessages(selectedChat.id);
      }, 5000); // Poll every 5 seconds for new messages

      return () => {
        clearInterval(messagePolling);
      };
    }
  }, [selectedChat]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChats = async () => {
    try {
      const { data, error } = await supabase
        .from('chats')
        .select(`
          id,
          listing_id,
          student_id,
          owner_id,
          blocked,
          created_at,
          listings(title)
        `)
        .or(`student_id.eq.${user?.id},owner_id.eq.${user?.id}`)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch additional info for each chat
      const enrichedChats = await Promise.all(
        (data || []).map(async (chat) => {
          const otherUserId = chat.student_id === user?.id ? chat.owner_id : chat.student_id;
          
          // Fetch other user's profile
          const { data: otherUser } = await supabase
            .from('profiles')
            .select('full_name, avatar_url')
            .eq('id', otherUserId)
            .single();

          // Fetch last message
          const { data: lastMessage } = await supabase
            .from('messages')
            .select('body_masked')
            .eq('chat_id', chat.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          return {
            ...chat,
            other_user_name: otherUser?.full_name,
            other_user_avatar: otherUser?.avatar_url,
            listing_title: chat.listings?.title,
            last_message: lastMessage?.body_masked,
            unread_count: 0 // Would be calculated properly in real implementation
          };
        })
      );

      setChats(enrichedChats);
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (chatId: string) => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChat || sending) return;

    // Validate message content
    const validation = validateMessageContent(newMessage);
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    // Protect message content
    const protection = protectMessageContent(newMessage.trim());
    
    // Warn user if sensitive information was detected
    if (!protection.isClean) {
      toast.warning(
        t('sensitive_info_masked', 
          'تم إخفاء معلومات حساسة للحماية', 
          'Sensitive information has been masked for protection')
      );
    }

    setSending(true);
    try {
      const { error } = await supabase
        .from('messages')
        .insert({
          chat_id: selectedChat.id,
          sender_id: user?.id,
          body_raw: protection.originalContent,
          body_masked: protection.maskedContent
        });

      if (error) throw error;

      setNewMessage('');
      // Immediately fetch messages to show the new one
      await fetchMessages(selectedChat.id);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(t('error_sending_message', 'خطأ في إرسال الرسالة', 'Error sending message'));
    } finally {
      setSending(false);
    }
  };

  const initiateCall = (type: 'voice' | 'video') => {
    toast.info(t('call_feature_coming_soon', 'ميزة المكالمات قادمة قريباً', 'Call feature coming soon'));
  };

  if (!user) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-muted-foreground">
            {t('login_to_message', 'يرجى تسجيل الدخول للمراسلة', 'Please log in to access messaging')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[600px]">
      {/* Chat List */}
      <Card className="lg:col-span-1">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            {t('messages', 'الرسائل', 'Messages')}
          </CardTitle>
          <CardDescription>
            {t('your_conversations', 'محادثاتك', 'Your conversations')}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[500px]">
            {loading ? (
              <div className="space-y-3 p-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-muted rounded-full animate-pulse" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted animate-pulse rounded w-3/4" />
                      <div className="h-3 bg-muted animate-pulse rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : chats.length === 0 ? (
              <div className="text-center py-8 px-4">
                <MessageCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {t('no_conversations', 'لا توجد محادثات', 'No conversations yet')}
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {chats.map((chat) => (
                  <div
                    key={chat.id}
                    className={`flex items-center gap-3 p-4 cursor-pointer hover:bg-muted/50 ${
                      selectedChat?.id === chat.id ? 'bg-muted' : ''
                    }`}
                    onClick={() => setSelectedChat(chat)}
                  >
                    <Avatar>
                      <AvatarImage src={chat.other_user_avatar} />
                      <AvatarFallback>
                        {chat.other_user_name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium truncate">
                          {chat.other_user_name}
                        </span>
                        {chat.unread_count > 0 && (
                          <Badge variant="destructive" className="text-xs">
                            {chat.unread_count}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {chat.listing_title}
                      </p>
                      {chat.last_message && (
                        <p className="text-xs text-muted-foreground truncate">
                          {chat.last_message}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Chat Window */}
      <Card className="lg:col-span-2">
        {selectedChat ? (
          <>
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarImage src={selectedChat.other_user_avatar} />
                    <AvatarFallback>
                      {selectedChat.other_user_name?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <CardTitle className="text-lg">
                      {selectedChat.other_user_name}
                    </CardTitle>
                    <CardDescription>
                      {selectedChat.listing_title}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => initiateCall('voice')}
                  >
                    <Phone className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => initiateCall('video')}
                  >
                    <Video className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0 flex flex-col h-[450px]">
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${
                        message.sender_id === user?.id ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          message.sender_id === user?.id
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}
                      >
                        <p className="text-sm">{safeDisplayContent(message.body_masked)}</p>
                        <p className="text-xs opacity-70 mt-1">
                          {new Date(message.created_at).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>
              <div className="border-t p-4">
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm">
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm">
                    <Image className="h-4 w-4" />
                  </Button>
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={t('type_message', 'اكتب رسالة...', 'Type a message...')}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    className="flex-1"
                  />
                  <Button onClick={sendMessage} disabled={sending}>
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </>
        ) : (
          <CardContent className="flex items-center justify-center h-full">
            <div className="text-center">
              <MessageCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {t('select_conversation', 'اختر محادثة للبدء', 'Select a conversation to start messaging')}
              </p>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default EnhancedMessaging;
